'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CollegeCostCalculator() {
  const t = useTranslations("tools.collegeCostCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    todayCost: 29910,
    costIncrease: 5,
    collegeLength: 4,
    savingPercent: 35,
    balanceNow: 0,
    returnRate: 5,
    taxRate: 25,
    startIn: 3,
  });

  const [results, setResults] = useState<any>(null);
  const [activePreset, setActivePreset] = useState('inState');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number): string => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const calculateFutureValue = (presentValue: number, rate: number, years: number): number => {
    return presentValue * Math.pow(1 + rate / 100, years);
  };

  const handlePresetClick = (preset: string, cost: number) => {
    setActivePreset(preset);
    setInputs({ ...inputs, todayCost: cost });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { todayCost, costIncrease, collegeLength, savingPercent, balanceNow, returnRate, taxRate, startIn } = inputs;

    const afterTaxReturn = returnRate * (1 - taxRate / 100);

    let totalCost = 0;
    const yearlyBreakdown = [];

    for (let i = 0; i < collegeLength; i++) {
      const yearFromNow = startIn + i;
      const annualCost = todayCost * Math.pow(1 + costIncrease / 100, yearFromNow);
      totalCost += annualCost;

      yearlyBreakdown.push({
        year: i + 1,
        yearDate: new Date().getFullYear() + yearFromNow,
        annualCost,
        cumulativeCost: totalCost,
      });
    }

    const futureSavingsValue = calculateFutureValue(balanceNow, afterTaxReturn, startIn);
    const savingsNeeded = totalCost * (savingPercent / 100);
    const additionalSavingsNeeded = Math.max(0, savingsNeeded - futureSavingsValue);

    let monthlySavingsRequired = 0;
    if (startIn > 0 && additionalSavingsNeeded > 0) {
      const monthlyRate = afterTaxReturn / 100 / 12;
      const months = startIn * 12;
      if (monthlyRate > 0) {
        monthlySavingsRequired = additionalSavingsNeeded * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
      } else {
        monthlySavingsRequired = additionalSavingsNeeded / months;
      }
    }

    const fromSavings = Math.min(savingsNeeded, futureSavingsValue + additionalSavingsNeeded);
    const fromOther = totalCost - fromSavings;

    setResults({
      totalCost,
      fromSavings,
      fromOther,
      yearlyBreakdown,
      currentSavings: balanceNow,
      futureSavings: futureSavingsValue,
      additionalNeeded: additionalSavingsNeeded,
      monthlySavings: monthlySavingsRequired,
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    {},
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const inputHintStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginTop: '0.25rem',
  };

  const presetSelectorStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginTop: '0.5rem',
  };

  const presetBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      padding: '0.5rem 1rem',
      background: isActive ? '#2563eb' : '#f1f5f9',
      border: `2px solid ${isActive ? '#2563eb' : '#e2e8f0'}`,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.85rem',
      transition: 'all 0.2s ease',
      color: isActive ? 'white' : '#475569',
    },
    {},
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1.5rem',
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginBottom: '2rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultCardStyle = (highlight: boolean) => getResponsiveStyle(
    {
      background: highlight ? 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' : 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      borderLeft: `4px solid ${highlight ? '#7c3aed' : '#2563eb'}`,
    },
    { padding: '1.25rem' },
    { padding: '1.4rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const resultValueStyle = (highlight: boolean) => getResponsiveStyle(
    {
      display: 'block',
      fontWeight: 700,
      color: highlight ? '#7c3aed' : colors.textPrimary,
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const breakdownTableStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      fontWeight: 700,
      color: colors.textPrimary,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
    },
    { padding: '0.75rem', fontSize: '0.8rem' },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const tdStyle = getResponsiveStyle(
    {
      color: '#475569',
      borderBottom: '1px solid #e2e8f0',
      textAlign: 'left' as const,
    },
    { padding: '0.75rem', fontSize: '0.85rem' },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const totalRowStyle: React.CSSProperties = {
    background: '#f1f5f9',
    fontWeight: 700,
    color: colors.textPrimary,
  };

  const savingsSectionStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    { padding: '1.25rem' },
    { padding: '1.4rem' },
    { padding: '1.5rem' }
  );

  const savingsH4Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: colors.textPrimary },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const savingsGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(4, 1fr)' }
  );

  const savingsItemStyle: React.CSSProperties = {
    padding: '1rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    borderLeft: '3px solid #f59e0b',
  };

  const savingsItemLabelStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.25rem',
  };

  const savingsItemValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="todayCost">{t("todayCostLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="todayCost"
                step="0.01"
                min="0"
                value={inputs.todayCost}
                onChange={(e) => setInputs({ ...inputs, todayCost: parseFloat(e.target.value) || 0 })}
                required
              />
              <div style={presetSelectorStyle}>
                <button
                  type="button"
                  style={presetBtnStyle(activePreset === 'private')}
                  onClick={() => handlePresetClick('private', 62990)}
                >
                  {t("presetPrivate")}
                </button>
                <button
                  type="button"
                  style={presetBtnStyle(activePreset === 'inState')}
                  onClick={() => handlePresetClick('inState', 29910)}
                >
                  {t("presetInState")}
                </button>
                <button
                  type="button"
                  style={presetBtnStyle(activePreset === 'outState')}
                  onClick={() => handlePresetClick('outState', 49080)}
                >
                  {t("presetOutState")}
                </button>
                <button
                  type="button"
                  style={presetBtnStyle(activePreset === 'twoYear')}
                  onClick={() => handlePresetClick('twoYear', 20570)}
                >
                  {t("presetTwoYear")}
                </button>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="costIncrease">{t("costIncreaseLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="costIncrease"
                step="0.1"
                min="0"
                max="100"
                value={inputs.costIncrease}
                onChange={(e) => setInputs({ ...inputs, costIncrease: parseFloat(e.target.value) || 0 })}
                required
              />
              <span style={inputHintStyle}>{t("costIncreaseHint")}</span>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="collegeLength">{t("collegeLengthLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="collegeLength"
                step="1"
                min="1"
                max="10"
                value={inputs.collegeLength}
                onChange={(e) => setInputs({ ...inputs, collegeLength: parseInt(e.target.value) || 1 })}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="savingPercent">{t("savingPercentLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="savingPercent"
                step="1"
                min="0"
                max="100"
                value={inputs.savingPercent}
                onChange={(e) => setInputs({ ...inputs, savingPercent: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="balanceNow">{t("balanceNowLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="balanceNow"
                step="0.01"
                min="0"
                value={inputs.balanceNow}
                onChange={(e) => setInputs({ ...inputs, balanceNow: parseFloat(e.target.value) || 0 })}
                required
              />
              <span style={inputHintStyle}>{t("balanceNowHint")}</span>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="returnRate">{t("returnRateLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="returnRate"
                step="0.1"
                min="0"
                max="100"
                value={inputs.returnRate}
                onChange={(e) => setInputs({ ...inputs, returnRate: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="taxRate">{t("taxRateLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="taxRate"
                step="0.1"
                min="0"
                max="100"
                value={inputs.taxRate}
                onChange={(e) => setInputs({ ...inputs, taxRate: parseFloat(e.target.value) || 0 })}
                required
              />
              <span style={inputHintStyle}>{t("taxRateHint")}</span>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="startIn">{t("startInLabel")}</label>
              <input
                style={inputStyle}
                type="number"
                id="startIn"
                step="1"
                min="0"
                max="30"
                value={inputs.startIn}
                onChange={(e) => setInputs({ ...inputs, startIn: parseInt(e.target.value) || 0 })}
                required
              />
            </div>

            <button
              type="submit"
              style={calculateBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(245, 158, 11, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>

              <div style={resultGridStyle}>
                <div style={resultCardStyle(true)}>
                  <span style={resultLabelStyle}>{t("totalCostLabel")}</span>
                  <span style={resultValueStyle(true)}>{formatCurrency(results.totalCost)}</span>
                </div>

                <div style={resultCardStyle(false)}>
                  <span style={resultLabelStyle}>{t("fromSavingsLabel")}</span>
                  <span style={resultValueStyle(false)}>{formatCurrency(results.fromSavings)}</span>
                </div>

                <div style={resultCardStyle(false)}>
                  <span style={resultLabelStyle}>{t("fromOtherLabel")}</span>
                  <span style={resultValueStyle(false)}>{formatCurrency(results.fromOther)}</span>
                </div>
              </div>

              <div style={breakdownTableStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("yearColumn")}</th>
                      <th style={thStyle}>{t("annualCostColumn")}</th>
                      <th style={thStyle}>{t("cumulativeCostColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.yearlyBreakdown.map((row: any, index: number) => (
                      <tr key={index}>
                        <td style={tdStyle}>{t("yearRow", { year: row.year, yearDate: row.yearDate })}</td>
                        <td style={tdStyle}>{formatCurrency(row.annualCost)}</td>
                        <td style={tdStyle}>{formatCurrency(row.cumulativeCost)}</td>
                      </tr>
                    ))}
                    <tr style={totalRowStyle}>
                      <td style={{ ...tdStyle, ...totalRowStyle }} colSpan={2}>{t("totalRow")}</td>
                      <td style={{ ...tdStyle, ...totalRowStyle }}>{formatCurrency(results.totalCost)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={savingsSectionStyle}>
                <h4 style={savingsH4Style}>{t("savingsPlanTitle")}</h4>
                <div style={savingsGridStyle}>
                  <div style={savingsItemStyle}>
                    <div style={savingsItemLabelStyle}>{t("currentSavingsLabel")}</div>
                    <div style={savingsItemValueStyle}>{formatCurrency(results.currentSavings)}</div>
                  </div>
                  <div style={savingsItemStyle}>
                    <div style={savingsItemLabelStyle}>{t("futureSavingsLabel")}</div>
                    <div style={savingsItemValueStyle}>{formatCurrency(results.futureSavings)}</div>
                  </div>
                  <div style={savingsItemStyle}>
                    <div style={savingsItemLabelStyle}>{t("additionalNeededLabel")}</div>
                    <div style={savingsItemValueStyle}>{formatCurrency(results.additionalNeeded)}</div>
                  </div>
                  <div style={savingsItemStyle}>
                    <div style={savingsItemLabelStyle}>{t("monthlySavingsLabel")}</div>
                    <div style={savingsItemValueStyle}>{formatCurrency(results.monthlySavings)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li>{t("howToStep1")}</li>
          <li>{t("howToStep2")}</li>
          <li>{t("howToStep3")}</li>
          <li>{t("howToStep4")}</li>
          <li>{t("howToStep5")}</li>
          <li>{t("howToStep6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>
        <ul style={listStyle}>
          <li>{t("detailsItem1")}</li>
          <li>{t("detailsItem2")}</li>
          <li>{t("detailsItem3")}</li>
          <li>{t("detailsItem4")}</li>
          <li>{t("detailsItem5")}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>
        <ul style={listStyle}>
          <li>{t("detailsItem6")}</li>
          <li>{t("detailsItem7")}</li>
          <li>{t("detailsItem8")}</li>
          <li>{t("detailsItem9")}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li>
            <a href="https://www.ruffalonl.com/enrollment-management-solutions/optimizing-yield/net-price-calculator-truecost-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref1")}
            </a>
          </li>
          <li>
            <a href="https://www.affordablecollegesonline.org/financial-aid/college-calculators/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref2")}
            </a>
          </li>
          <li>
            <a href="https://www.uaspire.org/news-events/uaspire-s-college-cost-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref3")}
            </a>
          </li>
          <li>
            <a href="https://www.insidehighered.com/news/students/financial-aid/2025/11/05/cost-comparison-tool-includes-all-student-expenses" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref4")}
            </a>
          </li>
          <li>
            <a href="https://blog.getintocollege.com/using-a-college-net-price-calculator-to-estimate-costs/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref5")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}